import { useCallback, useEffect, useState } from 'react';

import { MqSwitch } from '@fhs/ui';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';

import { GiftCardActionSheet } from '../../components/gift-cards-action-sheet';
import { CloseConfirmationModal } from '../../components/gift-cards-action-sheet/close-confirmation-modal';
import { useGiftCardsActionSheet } from '../../components/gift-cards-action-sheet/use-gift-cards-action-sheet';
import { GiftCardsTransactionHistoryActionSheet } from '../../components/gift-cards-transaction-history-action-sheet';
import { useLazyGetGiftCardDataQuery } from '../../queries/use-get-gift-card-data';
import { useGiftCardContext } from '../../state/gift-card';
import {
  logAddGiftCardPress,
  logCheckBalancePress,
  logGiftCardApiResponse,
  logGiftCardsPageView,
  logTransactionHistoryPress,
  logViewDigitalGiftCardPress,
} from '../../utils/logging-utils';

import { GiftCardsDesktop } from './gift-cards-desktop';
import { GiftCardsMobile } from './gift-cards-mobile';

export function GiftCards() {
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const { logRBIEvent } = useCRMEventsContext();

  useEffect(() => {
    logGiftCardsPageView(logRBIEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    actionSheetOpen: addGiftCardActionSheetOpen,
    handleActionSheetOpen: handleAddGiftCardActionSheetOpen,
    handleClose: handleAddGiftCardClose,
    handleRequestClose: handleAddGiftCardRequestClose,
    currentStep: currentAddGiftCardActionSheetStep,
    setCurrentStep: setCurrentAddGiftCardActionSheetStep,
    headerTitle: addGiftCardActionSheetHeaderTitle,
    closeConfirmationOpen: addGiftCardCloseConfirmationOpen,
    handleConfirmStay: handleAddGiftCardConfirmStay,
    handleConfirmLeave: handleAddGiftCardConfirmLeave,
    cardId: addGiftCardCardId,
    setCardId: setAddGiftCardCardId,
    pin: addGiftCardPin,
    setPin: setAddGiftCardPin,
  } = useGiftCardsActionSheet();

  const {
    actionSheetOpen: checkBalanceActionSheetOpen,
    handleActionSheetOpen: handleCheckBalanceActionSheetOpen,
    handleClose: handleCheckBalanceClose,
    handleRequestClose: handleCheckBalanceRequestClose,
    currentStep: currentCheckBalanceActionSheetStep,
    setCurrentStep: setCurrentCheckBalanceActionSheetStep,
    headerTitle: checkBalanceActionSheetHeaderTitle,
    closeConfirmationOpen: checkBalanceCloseConfirmationOpen,
    handleConfirmStay: handleCheckBalanceConfirmStay,
    handleConfirmLeave: handleCheckBalanceConfirmLeave,
    cardId: checkBalanceCardId,
    setCardId: setCheckBalanceCardId,
    pin: checkBalancePin,
    setPin: setCheckBalancePin,
  } = useGiftCardsActionSheet({ isCheckBalanceFlow: true });

  const { lastDigits, currentBalance, isBalanceLoading, handleBalanceUpdate, handleError } =
    useGiftCardContext();

  const [isCardNumberRedacted, setIsCardNumberRedacted] = useState(true);

  const { query, doQuery: triggerGiftCardDataQuery } = useLazyGetGiftCardDataQuery();
  const cardDataLoading = query.isLoading || query.isFetching;
  const cardNumber = query.data?.giftCardData?.cardNumber;
  const pin = query.data?.giftCardData?.pin;

  const onPressShowCardNumber = useCallback(async () => {
    if (isCardNumberRedacted) {
      logViewDigitalGiftCardPress(logRBIEvent);
    }
    triggerGiftCardDataQuery();
    setIsCardNumberRedacted(redacted => !redacted);
  }, [isCardNumberRedacted, logRBIEvent, triggerGiftCardDataQuery]);

  const onAddGiftCardPress = useCallback(() => {
    logAddGiftCardPress(logRBIEvent);
    handleAddGiftCardActionSheetOpen();
  }, [logRBIEvent, handleAddGiftCardActionSheetOpen]);

  const onCheckBalancePress = useCallback(() => {
    logCheckBalancePress(logRBIEvent);
    handleCheckBalanceActionSheetOpen();
  }, [logRBIEvent, handleCheckBalanceActionSheetOpen]);

  const onAddGiftCardConfirmLeave = useCallback(() => {
    logGiftCardApiResponse(logRBIEvent, 'Add Gift Card', 'failure');
    handleAddGiftCardConfirmLeave();
  }, [logRBIEvent, handleAddGiftCardConfirmLeave]);

  const onCheckBalanceConfirmLeave = useCallback(() => {
    logGiftCardApiResponse(logRBIEvent, 'Check Balance', 'failure');
    handleCheckBalanceConfirmLeave();
  }, [logRBIEvent, handleCheckBalanceConfirmLeave]);

  const onSeeHistoryPress = useCallback(() => {
    logTransactionHistoryPress(logRBIEvent);
    setShowTransactionHistory(true);
  }, [logRBIEvent]);

  return (
    <>
      <MqSwitch
        $ltDesktop={() => (
          <GiftCardsMobile
            onAddGiftCardPress={onAddGiftCardPress}
            onCheckBalancePress={onCheckBalancePress}
            lastDigits={lastDigits}
            onPressShowCardNumber={onPressShowCardNumber}
            isCardNumberRedacted={isCardNumberRedacted}
            isCardNumberLoading={cardDataLoading}
            cardNumber={cardNumber}
            pin={pin}
            onSeeHistoryPress={onSeeHistoryPress}
            isDigitalBalanceLoading={isBalanceLoading}
          />
        )}
        $gteDesktop={() => (
          <GiftCardsDesktop
            onAddGiftCardPress={onAddGiftCardPress}
            onCheckBalancePress={onCheckBalancePress}
            lastDigits={lastDigits}
            onPressShowCardNumber={onPressShowCardNumber}
            isCardNumberRedacted={isCardNumberRedacted}
            isCardNumberLoading={cardDataLoading}
            cardNumber={cardNumber}
            pin={pin}
            onSeeHistoryPress={onSeeHistoryPress}
            isDigitalBalanceLoading={isBalanceLoading}
          />
        )}
      />
      <GiftCardActionSheet
        headerTitle={addGiftCardActionSheetHeaderTitle}
        isVisible={addGiftCardActionSheetOpen}
        currentStep={currentAddGiftCardActionSheetStep}
        currentDigitalBalance={currentBalance}
        handleBalanceUpdate={handleBalanceUpdate}
        handleError={handleError}
        setCurrentStep={setCurrentAddGiftCardActionSheetStep}
        onClose={handleAddGiftCardRequestClose}
        onGiftCardSaved={handleAddGiftCardClose}
        cardId={addGiftCardCardId}
        onCardIdChange={setAddGiftCardCardId}
        pin={addGiftCardPin}
        onPinChange={setAddGiftCardPin}
      />
      <GiftCardActionSheet
        headerTitle={checkBalanceActionSheetHeaderTitle}
        isVisible={checkBalanceActionSheetOpen}
        isCheckBalanceFlow
        currentStep={currentCheckBalanceActionSheetStep}
        currentDigitalBalance={currentBalance}
        handleBalanceUpdate={handleBalanceUpdate}
        handleError={handleError}
        setCurrentStep={setCurrentCheckBalanceActionSheetStep}
        onClose={handleCheckBalanceRequestClose}
        onGiftCardSaved={handleCheckBalanceClose}
        cardId={checkBalanceCardId}
        onCardIdChange={setCheckBalanceCardId}
        pin={checkBalancePin}
        onPinChange={setCheckBalancePin}
      />
      {showTransactionHistory && (
        <GiftCardsTransactionHistoryActionSheet
          isVisible
          onClose={() => setShowTransactionHistory(false)}
        />
      )}
      {/* Confirmation modals rendered at parent level to avoid nested Modal issue on iOS */}
      <CloseConfirmationModal
        isOpen={addGiftCardCloseConfirmationOpen}
        onConfirm={handleAddGiftCardConfirmStay}
        onDismiss={onAddGiftCardConfirmLeave}
      />
      <CloseConfirmationModal
        isOpen={checkBalanceCloseConfirmationOpen}
        onConfirm={handleCheckBalanceConfirmStay}
        onDismiss={onCheckBalanceConfirmLeave}
      />
    </>
  );
}
